import { useState } from 'react'

import { t } from '../../../common/i18n.js'
import { LEAVING_REASONS, TURNOVER_IMPACTS } from '../../../common/leaving-types.js'
import { Language } from '../../../common/types/i18n.js'
import { Button } from '../button/button.js'
import { Select } from '../forms/select/select.js'
import { TextField } from '../forms/text-field/text-field.js'
import { BaseModal } from '../modal/base-modal/base-modal.js'
import './portal-offboard-modal.css'

/** The leaving details recorded by the "Lõpeta töösuhe" form (TASK-403). */
export interface OffboardFormData {
    employment_end_date: string
    leaving_file: string | null
    turnover_impact: string | null
    leaving_reason: string | null
}

interface PortalOffboardModalProps {
    isOpen: boolean
    lang: Language
    onClose: () => void
    /** Resolves true on success — the caller closes the modal then. */
    onSubmit: (data: OffboardFormData) => Promise<boolean>
}

/**
 * The "Lõpeta töösuhe" form: records the termination date and the leaving
 * classifiers instead of flipping the status outright — the person stays
 * active until their last day has passed.
 */
export const PortalOffboardModal = ({
    isOpen,
    lang,
    onClose,
    onSubmit,
}: PortalOffboardModalProps) => {
    const ph = t.portal.profile

    const [endDate, setEndDate] = useState('')
    const [leavingFile, setLeavingFile] = useState('')
    const [turnoverImpact, setTurnoverImpact] = useState('')
    const [leavingReason, setLeavingReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)

    const submit = () => {
        if (!endDate) return
        setIsSubmitting(true)
        void onSubmit({
            employment_end_date: endDate,
            leaving_file: leavingFile || null,
            turnover_impact: turnoverImpact || null,
            leaving_reason: leavingReason || null,
        }).then(() => setIsSubmitting(false))
    }

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            disableBackdropClose={isSubmitting}
            className="portal-offboard-modal"
            showClose
            closeAriaLabel={t.common.cancel(lang)}
        >
            <div className="portal-offboard-modal__header">
                <h3 className="portal-offboard-modal__title">{ph.offboardAction(lang)}</h3>
            </div>

            <div className="portal-offboard-modal__form-group">
                <label className="portal-offboard-modal__form-label">
                    {ph.terminationDate(lang)}
                </label>
                <TextField
                    inputProps={{ type: 'date' }}
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                />
            </div>

            <div className="portal-offboard-modal__form-group">
                <label className="portal-offboard-modal__form-label">{ph.leavingFile(lang)}</label>
                <TextField
                    inputProps={{ type: 'url', maxLength: 500 }}
                    value={leavingFile}
                    placeholder="https://..."
                    onChange={(value) => setLeavingFile(value)}
                />
            </div>

            <div className="portal-offboard-modal__form-group">
                <label className="portal-offboard-modal__form-label">
                    {ph.turnoverImpact(lang)}
                </label>
                <Select
                    className="portal-offboard-modal__form-select"
                    placeholder="—"
                    value={turnoverImpact}
                    onChange={(value) => setTurnoverImpact(value)}
                    options={TURNOVER_IMPACTS.map((key) => ({
                        value: key,
                        label: ph.turnoverImpactLevels[key](lang),
                    }))}
                />
            </div>

            <div className="portal-offboard-modal__form-group">
                <label className="portal-offboard-modal__form-label">
                    {ph.leavingReason(lang)}
                </label>
                <Select
                    className="portal-offboard-modal__form-select"
                    placeholder="—"
                    value={leavingReason}
                    onChange={(value) => setLeavingReason(value)}
                    options={LEAVING_REASONS.map((key) => ({
                        value: key,
                        label: ph.leavingReasons[key](lang),
                    }))}
                />
            </div>

            <Button
                className="portal-offboard-modal__submit"
                size="medium"
                variant="primary"
                text={isSubmitting ? ph.savingProfile(lang) : ph.offboardAction(lang)}
                isDisabled={isSubmitting || !endDate}
                onClick={submit}
            />
        </BaseModal>
    )
}
